import { DropzoneAreaBase, FileObject } from 'react-mui-dropzone';
import heic2any from 'heic2any';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@mui/material';
import { Cloud } from '../common/ui-component/icons';
import { Page } from '../photos/ui';
import { isMobile } from 'react-device-detect';

interface Props {
  onSuccess: (files: FileObject[]) => void;
  onDelete: (file: FileObject, index: number) => void;
  filesLimit?: number;
  files: FileObject[];
  onUploadClick: () => void;
}

const UploadFiles = ({ onSuccess, onDelete, files, filesLimit = 20, onUploadClick }: Props) => {
  const { t } = useTranslation('photosPage');
  const [open, setOpen] = React.useState(false);

  const getConvertedFiles = async (filesToConvert: FileObject[]): Promise<FileObject[]> => {
    const filesToAdd: FileObject[] = [];
    for await (const fileToConvert of filesToConvert) {
      if (
        fileToConvert.file.type === 'image/heic' ||
        fileToConvert.file.type === 'image/heif' ||
        fileToConvert.file.name.endsWith('.heic') ||
        fileToConvert.file.name.endsWith('.heif')
      ) {
        const blob = await heic2any({ blob: fileToConvert.file, toType: 'image/jpeg', quality: 1 });
        const file = new File(Array.isArray(blob) ? blob : [blob], fileToConvert.file.name, { type: 'image/jpeg' });
        filesToAdd.push({ file, data: fileToConvert.data });
      } else {
        filesToAdd.push(fileToConvert);
      }
    }

    return filesToAdd;
  };

  const handleAddPhotos = async (newFiles: FileObject[]) => {
    const filesToAdd = await getConvertedFiles(newFiles);
    setOpen(false);
    onSuccess(filesToAdd);
  };

  const handleDelete = (deletedFileObject: FileObject, index: number) => {
    onDelete(deletedFileObject, index);
  };

  const handleAddMobilePhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = (event.target.files || [])[0];
    const reader = new FileReader();
    reader.onload = () => {
      handleAddPhotos([{ file, data: reader.result }]);
    };
    reader.readAsArrayBuffer(file);
  };

  return isMobile ? (
    <>
      <label
        htmlFor="uploadMobilefile"
        className="tw-font-inter tw-bg-primary tw-border-primary tw-p-3
         tw-rounded-full tw-cursor-pointer tw-hover:tw-text-white tw-text-white"
      >
        {t('upload')}
      </label>
      <input
        id="uploadMobilefile"
        type="file"
        accept="image/*, .heic, .heif"
        data-upload-type="single"
        style={{ display: 'none' }}
        onChange={(event) => handleAddMobilePhoto(event)}
        onClick={() => onUploadClick()}
      />
    </>
  ) : (
    <>
      <Page.Button
        onClick={() => {
          onUploadClick();
          setOpen(true);
        }}
      >
        <Page.Button.Primary>{t('upload')}</Page.Button.Primary>
      </Page.Button>
      <Dialog open={open}>
        <div className="[&_button]:tw-rounded-full">
          <div className="tw-p-4">
            <h2 className="tw-w-full tw-text-primary tw-text-2xl tw-text-center">
              <span>{t('uploadFile')}</span>
            </h2>
            <DropzoneAreaBase
              classes={{
                root: 'tw-border-solid',
                textContainer: 'tw-p-10 [&_p]:tw-pb-6',
              }}
              fileObjects={files}
              filesLimit={filesLimit}
              onAdd={handleAddPhotos}
              onDelete={handleDelete}
              acceptedFiles={['image/*', 'image/heic', 'image/heif', '.heic', '.heif']}
              showPreviews={false}
              showPreviewsInDropzone={false}
              showAlerts={true}
              Icon={Cloud}
              maxFileSize={12000000}
            />
            <div
              className={`tw-flex tw-w-full tw-justify-center tw-gap-4 tw-pt-4
              [&_*]:tw-text-xs [&_*]:tw-font-semibold [&_*]:tw-tracking-tighter [&_*]:tw-no-underline [&_*]:tw-uppercase
              [&_*]:tw-hover:tw-no-underline
              [&_*]:tw-rounded-full [&_*]:tw-min-w-36 
              [&_*]:tw-flex [&_*]:tw-justify-center
              `}
            >
              <Page.Button onClick={() => setOpen(false)}>
                <Page.Button.Custom
                  className="tw-border-solid 
                tw-bg-white tw-text-black tw-hover:tw-text-black tw-min-w-36"
                >
                  {t('cancel')}
                </Page.Button.Custom>
              </Page.Button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default UploadFiles;
